import React, { useState, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { QRCodeSVG } from "qrcode.react";
import { toPng, toBlob } from "html-to-image";
import { Check, X, Minus, Plus, Send, Plane, Download } from "lucide-react";
import { Chapter, MaskLines, Reveal } from "./Reveal";
import { HeartIcon, Stamp } from "./Deco";
import { WEDDING } from "../content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const seatFromName = (name) => {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const row = (h % 30) + 1;
  const col = "ABCDEF"[h % 6];
  return `${row}${col}`;
};

const fireConfetti = () => {
  const colors = ["#D4AF37", "#FBF5B7", "#B38728", "#BF953F", "#F5ECD8"];
  confetti({ particleCount: 130, spread: 80, startVelocity: 45, origin: { y: 0.55 }, colors });
  setTimeout(() => confetti({ particleCount: 80, angle: 60, spread: 65, origin: { x: 0, y: 0.7 }, colors }), 150);
  setTimeout(() => confetti({ particleCount: 80, angle: 120, spread: 65, origin: { x: 1, y: 0.7 }, colors }), 150);
};

export const Rsvp = ({ t, footer }) => {
  const demo =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("rsvpdemo");
  const [name, setName] = useState(demo ? "Ahmet Yılmaz" : "");
  const [attending, setAttending] = useState(demo ? "accept" : "");
  const [guests, setGuests] = useState(demo ? 2 : 1);
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(demo);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !attending) {
      toast.error(t.required);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/rsvp`, {
        name: name.trim(),
        attending,
        guest_count: attending === "accept" ? guests : 0,
        contact,
        message,
      });
      setDone(true);
      toast.success(t.success);
      fireConfetti();
    } catch (err) {
      toast.error(t.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="rsvp" className="relative leather py-24 sm:py-32 px-5">
      <div className="max-w-xl mx-auto text-center">
        <Chapter dark>{t.chapter}</Chapter>
        <div className="flex items-center justify-center gap-3">
          <HeartIcon size={16} className="text-[var(--gold)]" />
          <MaskLines lines={[t.title]} className="gold-text font-serif text-4xl sm:text-6xl" />
          <HeartIcon size={16} className="text-[var(--gold)]" />
        </div>
        <Reveal delay={0.1}>
          <p className="font-sans text-sm text-[var(--gold-light)]/70 mt-4">{t.note}</p>
        </Reveal>

        <Reveal delay={0.15}>
          {done ? (
            <div className="mt-12 rounded-2xl paper p-10 flex flex-col items-center" data-testid="rsvp-success">
              <motion.div
                initial={{ scale: 2.3, opacity: 0, rotate: -50 }}
                animate={{ scale: 1, opacity: 1, rotate: -14 }}
                transition={{ type: "spring", stiffness: 240, damping: 11, delay: 0.15 }}
                className="mb-4 text-[var(--gold-dark)]"
              >
                <Stamp />
              </motion.div>
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="font-serif text-2xl text-[var(--forest)]"
              >
                {t.success}
              </motion.p>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.75 }}
                className="font-script text-4xl text-[var(--gold-dark)] mt-4"
              >
                {footer.script}
              </motion.p>

              <PersonalPass
                name={name}
                attending={attending}
                guests={guests}
                t={t}
              />
            </div>
          ) : (
            <form onSubmit={submit} className="mt-12 rounded-2xl paper p-6 sm:p-9 text-left space-y-6" data-testid="rsvp-form">
              <Field label={t.name}>
                <input
                  data-testid="rsvp-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.namePh}
                  className="w-full bg-transparent border-b border-[var(--gold-dark)]/40 py-2 font-serif text-lg text-[var(--forest)] outline-none focus:border-[var(--gold-dark)] transition-colors placeholder:text-[var(--olive)]/40"
                />
              </Field>

              <Field label={t.status}>
                <div className="grid grid-cols-2 gap-3">
                  <StatusBtn active={attending === "accept"} onClick={() => setAttending("accept")}
                    icon={<Check size={16} />} label={t.accept} testid="rsvp-accept" />
                  <StatusBtn active={attending === "decline"} onClick={() => setAttending("decline")}
                    icon={<X size={16} />} label={t.decline} testid="rsvp-decline" />
                </div>
              </Field>

              {attending === "accept" && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
                  <Field label={t.guests}>
                    <div className="flex items-center gap-4">
                      <Stepper onClick={() => setGuests((g) => Math.max(1, g - 1))} testid="guest-minus"><Minus size={16} /></Stepper>
                      <span data-testid="guest-count" className="font-serif text-2xl text-[var(--forest)] w-8 text-center tabular-nums">{guests}</span>
                      <Stepper onClick={() => setGuests((g) => Math.min(10, g + 1))} testid="guest-plus"><Plus size={16} /></Stepper>
                    </div>
                  </Field>
                </motion.div>
              )}

              <Field label={t.contact}>
                <input
                  data-testid="rsvp-contact"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder={t.contactPh}
                  className="w-full bg-transparent border-b border-[var(--gold-dark)]/40 py-2 font-serif text-lg text-[var(--forest)] outline-none focus:border-[var(--gold-dark)] transition-colors placeholder:text-[var(--olive)]/40"
                />
              </Field>

              <Field label={t.message}>
                <textarea
                  data-testid="rsvp-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t.messagePh}
                  rows={2}
                  className="w-full bg-transparent border-b border-[var(--gold-dark)]/40 py-2 font-serif text-lg text-[var(--forest)] outline-none focus:border-[var(--gold-dark)] transition-colors resize-none placeholder:text-[var(--olive)]/40"
                />
              </Field>

              <button
                data-testid="rsvp-submit"
                type="submit"
                disabled={sending}
                className="group w-full flex items-center justify-center gap-3 py-3.5 rounded-full font-sans tracking-[0.18em] text-xs uppercase text-[var(--olive)] bg-gradient-to-r from-[#BF953F] via-[#FCF6BA] to-[#B38728] transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                <Send size={15} /> {sending ? t.sending : t.submit}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="font-sans text-[11px] tracking-[0.2em] uppercase text-[var(--olive)]/60 block mb-2">{label}</label>
    {children}
  </div>
);

const StatusBtn = ({ active, onClick, icon, label, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`flex items-center justify-center gap-2 py-3 rounded-lg border text-xs sm:text-sm font-sans tracking-wide transition-all duration-300 ${
      active
        ? "bg-[var(--forest)] text-[var(--cream)] border-[var(--forest)]"
        : "border-[var(--gold-dark)]/40 text-[var(--olive)]/70 hover:border-[var(--gold-dark)]"
    }`}
  >
    {icon} {label}
  </button>
);

const Stepper = ({ onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className="w-10 h-10 rounded-full border border-[var(--gold-dark)]/40 flex items-center justify-center text-[var(--forest)] hover:bg-[var(--forest)] hover:text-[var(--cream)] transition-colors">
    {children}
  </button>
);


const PersonalPass = ({ name, attending, guests, t }) => {
  const f = WEDDING.flight;
  const passRef = useRef(null);
  const going = attending === "accept";
  const seat = going ? seatFromName(name || "Guest") : "—";
  const status = going ? t.accept : t.decline;

  const downloadPng = async () => {
    if (!passRef.current) return;
    const fileName = `binis-karti-${(name || "misafir").replace(/\s+/g, "-").toLowerCase()}.png`;
    try {
      const opts = { pixelRatio: 2.5, cacheBust: true, skipFonts: true, backgroundColor: "#E8DCC2" };
      const blob = await toBlob(passRef.current, opts);
      if (!blob) throw new Error("blob null");

      const file = new File([blob], fileName, { type: "image/png" });
      const isTouch =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(pointer: coarse)").matches;
      const canShareFile =
        typeof navigator !== "undefined" &&
        navigator.canShare &&
        navigator.canShare({ files: [file] });

      // On phones/tablets, use the native share sheet (Save to Photos on iOS)
      if (isTouch && canShareFile) {
        try {
          await navigator.share({ files: [file], title: "Biniş Kartı" });
          toast.success(t.cardDownloaded || "Biniş kartınız hazır ✈");
          return;
        } catch (err) {
          if (err && err.name === "AbortError") return; // user cancelled
          // otherwise fall through to blob download / new tab
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // iOS Safari ignores download attr — open the image so the user can long-press "Save Image"
      if (isTouch) window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 8000);
      toast.success(t.cardDownloaded || "Biniş kartınız indirildi ✈");
    } catch (e) {
      console.error("Boarding pass download failed:", e);
      // last-resort: open the PNG in a new tab so it can be saved manually
      try {
        const dataUrl = await toPng(passRef.current, {
          pixelRatio: 2,
          cacheBust: true,
          skipFonts: true,
          backgroundColor: "#E8DCC2",
        });
        const w = window.open();
        if (w) {
          w.document.write(
            `<img src="${dataUrl}" style="width:100%;height:auto" alt="Biniş Kartı"/>`
          );
          toast.success("Kartı basılı tutup 'Görseli Kaydet' ile indirebilirsiniz.");
        } else {
          toast.error("Kart indirilemedi, tekrar deneyin.");
        }
      } catch (e2) {
        toast.error("Kart indirilemedi, tekrar deneyin.");
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.95, duration: 0.7 }}
      className="mt-8 w-full"
      data-testid="personal-pass"
    >
      <p className="font-mono text-[10px] tracking-[0.3em] text-[var(--olive)]/50 mb-3 uppercase">
        Kişisel Biniş Kartınız / Your Boarding Pass
      </p>
      <div ref={passRef} className="flex rounded-xl overflow-hidden border border-[var(--gold-dark)]/40 shadow-lg text-left">
        {/* stub */}
        <div className="leather flex items-center justify-center px-2 py-4">
          <span
            className="gold-text font-serif tracking-[0.25em] text-[10px] uppercase"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            Wedding Airlines
          </span>
        </div>
        {/* body */}
        <div className="flex-1 min-w-0 bg-[var(--ivory)] p-3 sm:p-4">
          <p className="font-mono text-[8px] tracking-[0.2em] text-[var(--olive)]/60 uppercase">
            Passenger / Yolcu
          </p>
          <p className="font-serif text-base sm:text-xl text-[var(--forest)] leading-tight truncate">
            {name}
          </p>
          <div className="mt-3 flex items-center gap-2 sm:gap-3">
            <div>
              <p className="font-mono text-[7px] tracking-[0.2em] text-[var(--olive)]/60">FROM</p>
              <p className="font-serif font-bold text-xl sm:text-2xl text-[var(--forest)] leading-none">{f.from}</p>
            </div>
            <div className="flex-1 flex items-center justify-center text-[var(--gold-dark)]">
              <span className="h-px flex-1 bg-current opacity-40" />
              <Plane size={13} className="mx-1 rotate-45" />
              <span className="h-px flex-1 bg-current opacity-40" />
            </div>
            <div className="text-right">
              <p className="font-mono text-[7px] tracking-[0.2em] text-[var(--olive)]/60">TO</p>
              <p className="font-serif font-bold text-xl sm:text-2xl text-[var(--forest)] leading-none">{f.to}</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-1">
            <PassCell label="FLIGHT" value={f.no} />
            <PassCell label="SEAT" value={seat} />
            <PassCell label="DURUM" value={status} />
          </div>
        </div>
        {/* qr */}
        <div className="bg-[var(--cream)] border-l border-dashed border-[var(--olive)]/40 flex items-center justify-center p-2 sm:p-3">
          <QRCodeSVG value={WEDDING.mapsUrl} size={60} bgColor="#F5ECD8" fgColor="#14241A" level="M" />
        </div>
      </div>

      <button
        data-testid="download-pass-btn"
        onClick={downloadPng}
        className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-sans tracking-[0.18em] text-[11px] uppercase text-[var(--olive)] bg-gradient-to-r from-[#BF953F] via-[#FCF6BA] to-[#B38728] transition-transform duration-300 hover:scale-[1.03] active:scale-95"
      >
        <Download size={14} /> {t.downloadCard || "Kartı İndir"}
      </button>
    </motion.div>
  );
};

const PassCell = ({ label, value }) => (
  <div>
    <p className="font-mono text-[7px] tracking-[0.15em] text-[var(--olive)]/55 uppercase">{label}</p>
    <p className="font-serif text-sm text-[var(--forest)] leading-tight">{value}</p>
  </div>
);
